"use client";
import { Typography } from "@material-tailwind/react";
import React, { useRef, useState } from "react";
import { SignatureCanvas as TSignatureCanvas } from "react-signature-canvas";
import dynamic from "next/dynamic";
const SignatureCanvas: any = dynamic(() => import("react-signature-canvas"), {
  ssr: false, // ห้าม SSR
});

interface Props {
  signatureImageFile: File | undefined;
  setSignatureImageFile: (file: File | undefined) => void;
}

const Sign = ({ signatureImageFile, setSignatureImageFile }: Props) => {
  const sigRef = useRef<TSignatureCanvas | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  const handleSignatureEnd = () => {
    if (sigRef.current) {
      setSignature(sigRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    setSignature(null);
    setSignatureImageFile(undefined);
  };

  const save = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert("กรุณาลงลายเซ็นก่อนบันทึก");
      return;
    }

    let dataURL: string;
    try {
      const trimmedCanvas = trimCanvas(sigRef.current.getCanvas());
      dataURL = trimmedCanvas.toDataURL("image/png");
    } catch (error) {
      console.error("Trim canvas error:", error);
      try {
        dataURL = sigRef.current.toDataURL("image/png");
      } catch (fallbackError) {
        console.error("Generate signature error:", fallbackError);
        alert("ไม่สามารถสร้างลายเซ็นได้ กรุณาลองใหม่");
        return;
      }
    }

    try {
      const response = await fetch(dataURL);
      if (!response.ok) {
        throw new Error(`Failed to fetch data URL: ${response.statusText}`);
      }
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Generated signature blob is empty");
      }

      const file = new File([blob], "signature.png", { type: "image/png" });
      setSignatureImageFile(file);
    } catch (error) {
      console.error("Create signature file error:", error);
      alert(
        `ไม่สามารถสร้างไฟล์ลายเซ็นได้: ${error instanceof Error ? error.message : "กรุณาลองใหม่"}`,
      );
    }
  };

  const trimCanvas = (function () {
    function rowBlank(imageData: any, width: any, y: any) {
      for (var x = 0; x < width; ++x) {
        if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
      }
      return true;
    }

    function columnBlank(
      imageData: any,
      width: any,
      x: any,
      top: any,
      bottom: any,
    ) {
      for (var y = top; y < bottom; ++y) {
        if (imageData.data[y * width * 4 + x * 4 + 3] !== 0) return false;
      }
      return true;
    }

    return function (canvas: any) {
      var ctx = canvas.getContext("2d");
      var width = canvas.width;
      var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      var top = 0,
        bottom = imageData.height,
        left = 0,
        right = imageData.width;

      while (top < bottom && rowBlank(imageData, width, top)) ++top;
      while (bottom - 1 > top && rowBlank(imageData, width, bottom - 1))
        --bottom;
      while (left < right && columnBlank(imageData, width, left, top, bottom))
        ++left;
      while (
        right - 1 > left &&
        columnBlank(imageData, width, right - 1, top, bottom)
      )
        --right;

      var trimmed = ctx.getImageData(left, top, right - left, bottom - top);
      var copy = canvas.ownerDocument.createElement("canvas");
      var copyCtx = copy.getContext("2d");
      copy.width = trimmed.width;
      copy.height = trimmed.height;
      copyCtx.putImageData(trimmed, 0, 0);

      return copy;
    };
  })();

  return (
    <div>
      <Typography
        variant="small"
        color="blue-gray"
        className="font-medium font-noto text-start"
      >
        ลายเซ็นสำหรับใบสมัครสมาชิกศรีกรุง
      </Typography>
      <div className="flex flex-col items-center justify-center gap-5">
        <SignatureCanvas
          penColor="blue"
          canvasProps={{
            className: "w-[400px] h-[200px] md:h-[200px] rounded-lg",
            style: {
              marginTop: "10px",
              border: "2px solid gray",
            },
          }}
          ref={sigRef}
          onEnd={handleSignatureEnd}
        />
        {signature && (
          <div className="flex flex-row gap-3">
            {signatureImageFile ? (
              <button
                type="button"
                className="border-2 border-orange-400 p-1 rounded-2xl w-[80px] text-orange-400"
                onClick={clearSignature}
              >
                เริ่มใหม่
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="border-2 border-red-200 p-1 rounded-2xl w-[80px] text-red-400"
                  onClick={clearSignature}
                >
                  ลบ
                </button>
                <button
                  type="button"
                  className="border-2 border-green-500 p-1 rounded-2xl w-[80px] text-green-500"
                  onClick={save}
                >
                  บันทึก
                </button>
              </>
            )}
          </div>
        )}
        {!signature && (
          <div className="text-sm text-gray-500 mt-2">
            กรุณาลงลายเซ็นในช่องด้านบน
          </div>
        )}
      </div>
    </div>
  );
};

export default Sign;
